import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { fmt } from "@/pages/admin/utils/format";



// ---------------------------------------------------------------------------
// Iteration 41 — Admin: send WhatsApp broadcast to all customers
// ---------------------------------------------------------------------------
const AdminWhatsAppBroadcast = () => {
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [audience, setAudience] = useState(null);  // unique-phones count
  const [sending, setSending] = useState(false);
  const [last, setLast] = useState(null);
  const [history, setHistory] = useState([]);
  const [testPhone, setTestPhone] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const sendSingleTest = async () => {
    const to = testPhone.trim();
    if (!to) { toast.error("أدخلي رقم الاختبار بصيغة دولية (+9665...)"); return; }
    if (!body.trim()) { toast.error("يرجى كتابة نص الرسالة أولًا"); return; }
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/whatsapp-broadcast/test-single", {
        to, body, title: title.trim() || undefined,
      });
      setTestResult(data);
      if (data?.result?.ok) toast.success("أُرسلت الرسالة التجريبية إلى رقم واحد");
      else toast.error(`لم تُرسل: ${data?.result?.error || "خطأ غير معروف"}`);
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر الإرسال التجريبي");
    } finally {
      setTestSending(false);
    }
  };

  const loadAudience = async () => {
    try {
      const r = await api.get("/admin/whatsapp-broadcast/preview-audience");
      setAudience(r.data?.audience_size ?? 0);
    } catch (err) { console.warn("admin: load preview-audience failed", err); }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/whatsapp-broadcasts");
      setHistory(r.data?.items || []);
    } catch (err) { console.warn("admin: load wa-broadcast history failed", err); }
  };

  useEffect(() => {
    loadAudience();
    loadHistory();
  }, []);

  const submit = async () => {
    if (!body.trim()) {
      toast.error("يرجى كتابة نص الرسالة");
      return;
    }
    if (audience === 0) {
      toast.error("لا يوجد عملاء برقم جوال صالح");
      return;
    }
    if (!window.confirm(
      `سيتم إرسال الرسالة عبر واتساب إلى ${audience} عميل (دفعات 30). هل تريد المتابعة؟`,
    )) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/whatsapp-broadcast", {
        body: body.trim(),
        title: title.trim() || undefined,
        batch_size: 30,
      });
      setLast(data);
      toast.success(
        `تم الإرسال إلى ${data.sent}/${data.audience_size}` +
        (data.failures ? ` (${data.failures} فشل)` : ""),
      );
      setBody("");
      loadHistory();
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر الإرسال");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-whatsapp-broadcast" className="space-y-6">
      <div className="card-warm p-5" dir="rtl">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="w-5 h-5 text-[#25D366]" />
          <h3 className="font-heading text-xl text-[#222222]">بث رسالة واتساب</h3>
        </div>
        <p className="text-sm text-[#5E5C58] leading-relaxed">
          تُرسل نفس الرسالة عبر واتساب لكل عملاء أوتادك (الذين لديهم رقم جوال
          مسجّل) على دفعات متتابعة (٣٠ رسالة لكل دفعة) لاحترام حدود مزوّد الخدمة.
          الجمهور الحالي:{" "}
          <strong className="text-[#0A4D3C]" data-testid="wa-broadcast-audience-size">
            {audience === null ? "..." : `${audience} عميل`}
          </strong>
        </p>

        <div className="mt-4">
          <label htmlFor="wa-bc-title" className="block text-xs text-[#5E5C58] mb-1">
            عنوان الرسالة (اختياري — الافتراضي: «عرض من أوتادك»)
          </label>
          <input
            id="wa-bc-title"
            data-testid="wa-broadcast-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="عرض من أوتادك"
            className="w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#25D366]"
          />
        </div>

        <div className="mt-4">
          <label htmlFor="wa-bc-body" className="block text-xs text-[#5E5C58] mb-1">
            نص الرسالة (يدعم أسطر متعددة — تُحوَّل تلقائيًا إلى سطر واحد عند الإرسال بحسب قيود واتساب)
          </label>
          <textarea
            id="wa-bc-body"
            data-testid="wa-broadcast-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="اكتب نص الرسالة هنا. يبدأ كل بث تلقائيًا بالتحية «مرحبًا [الاسم]»."
            className="w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#25D366]"
          />
          <div className="mt-1 text-xs text-[#8B8780]">{body.length} / 4000 حرف</div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button
            type="button"
            data-testid="wa-broadcast-send-btn"
            onClick={submit}
            disabled={sending || !body.trim() || !audience}
            className="rounded-full bg-[#25D366] hover:bg-[#1FB155] text-white"
          >
            <Send className="w-3.5 h-3.5 ms-2" />
            {sending ? "جاري الإرسال..." : `إرسال للجميع (${audience ?? 0})`}
          </Button>
          <span className="text-xs text-[#5E5C58]">
            * يعتمد على تفعيل WHATSAPP_ENABLED في إعدادات الخادم.
          </span>
        </div>

        <div
          data-testid="wa-broadcast-single-test"
          className="mt-5 rounded-xl border border-dashed border-[#0A4D3C]/40 bg-[#F6F4EE] p-4"
        >
          <p className="text-sm font-semibold text-[#0A4D3C] mb-1">إرسال تجريبي إلى رقم واحد</p>
          <p className="text-xs text-[#5E5C58] mb-3 leading-relaxed">
            يُرسل نفس القالب المعتمد بنفس العنوان والنص أعلاه إلى الرقم المُدخل فقط —
            دون إضافته للجمهور ودون بدء البث الكامل.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="wa-broadcast-test-phone-input"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              dir="ltr"
              placeholder="+9665XXXXXXXX"
              className="flex-1 rounded-xl border border-[#E5E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0A4D3C]"
            />
            <Button
              type="button"
              data-testid="wa-broadcast-test-single-btn"
              onClick={sendSingleTest}
              disabled={testSending || !testPhone.trim() || !body.trim()}
              variant="outline"
              className="rounded-full border-[#0A4D3C] text-[#0A4D3C] hover:bg-[#0A4D3C]/5"
            >
              {testSending ? "جاري الإرسال..." : "إرسال تجريبي إلى رقم واحد"}
            </Button>
          </div>
          {testResult && (
            <div
              data-testid="wa-broadcast-test-single-result"
              className="mt-3 text-xs text-[#5E5C58] break-words"
              dir="ltr"
            >
              <span className={testResult.result?.ok ? "text-emerald-700" : "text-rose-700"}>
                {testResult.result?.ok ? "ok" : `failed: ${testResult.result?.error}`}
              </span>
              {" — "}
              <code>{JSON.stringify(testResult.content_variables)}</code>
            </div>
          )}
        </div>

        {last && (
          <div
            data-testid="wa-broadcast-last-result"
            className="mt-4 rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-xs text-[#5E5C58]"
          >
            آخر إرسال: نجح {last.sent} من {last.audience_size}
            {last.failures ? ` — فشل: ${last.failures}` : ""}
            {" "}({last.batches} دفعة)
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="card-warm p-0 overflow-hidden" data-testid="wa-broadcast-history">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F4EE] text-[#5E5C58]">
              <tr>
                <th className="text-start p-4">المُرسل/الجمهور</th>
                <th className="text-start p-4">الحالة</th>
                <th className="text-start p-4">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-[#E5E1D8]">
                  <td className="p-4 text-xs text-[#5E5C58]">
                    <strong className="text-[#222222]">{h.sent ?? 0}</strong>
                    {" / "}
                    {h.audience_size}
                    {h.failures ? <span className="text-rose-700"> (فشل: {h.failures})</span> : null}
                  </td>
                  <td className="p-4">
                    <Badge
                      className={`rounded-full ${
                        h.status === "completed"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {h.status === "completed" ? "اكتمل" : h.status}
                    </Badge>
                  </td>
                  <td className="p-4 text-xs text-[#5E5C58]">{fmt(h.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { AdminWhatsAppBroadcast };
